import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GenPdf from '../GenPdf';
import Header from '../Header/Header';
import NumDossFacAmendment from '../NumDossFacAmendment';
import DocMenu from "../DocMenu";
import ListTable from './FacAmendmentTable/ListTable';
import { fetchAllFacAmendment } from "../../redux/actions/facAmendmentActions";
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import { getRefFacture, genFacAmendmentTable, getFacAmendmentTable, getAmendmentLetter } from './Utils';
import { numberToFormat, handleChangeDateToMonth } from "../Utils";
import "../../Styles/FactureAmendment/FacAmendmentTable.scss";

const safeParseJson = (value) => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

const FacAmendmentTable = () => {
  const dispatch = useDispatch();

  const dossierValue = useSelector(state => state.devis.dossierValue);
  const facAmendmentsByDossIndex = useSelector(state => state.facAmendment.facAmendmentsByDossIndex);
  const reminders = useSelector(state => state.reminderFacAmendment.items);
  const contractFacAmendmentIndex = useSelector(state => state.facAmendment.contractFacAmendmentIndex);

  const [facturesData, setFacturesData] = useState([]);
  const [totalHt, setTotalHt] = useState(0);
  const [totalTva, setTotalTva] = useState(0);
  const [totalTtc, setTotalTtc] = useState(0);
  const [openFile, setOpenFile] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [file, setFile] = useState(null);

  const indexAmendmentLetter = getAmendmentLetter(contractFacAmendmentIndex);

  // ─── handleGetReminder ──────────────────────────────────────────────────
  const handleGetReminder = useCallback((validation, date, days, numclient) => {
    const reminderSent = reminders.filter(r =>
      r.numclient === numclient &&
      (days === 15 ? r.first_reminder == 1 : r.first_reminder == 0)
    );
    const reminderDate = reminderSent.length > 0
      ? new Date(reminderSent[0].date_reminder).toLocaleDateString('en-CA')
      : '';

    const today = new Date();
    const dateFormated = new Date(date);
    dateFormated.setDate(dateFormated.getDate() + days);

    return reminderSent.length > 0 && today >= dateFormated && validation != 1
      ? reminderDate !== '' ? reminderDate : 'À ENVOYER'
      : '';
  }, [reminders]);

  // ─── loadData ────────────────────────────────────────────────────────────
  const loadData = useCallback((updateData = false, updatedFacturesData = null) => {
    dispatch(changePdfFileName(""));

    let processed = [];

    if (!updateData) {
      processed = [...facAmendmentsByDossIndex]
        .sort((a, b) => a.numclient.localeCompare(b.numclient))
        .map(item => ({
          ...item,
          // ✅ FIX : safeParseJson au lieu de JSON.parse direct
          dossier: safeParseJson(item.dossier),
          montant_tva: parseFloat((item.total_ht * (item.tva / 100)).toFixed(2)),
          amount_ca: item.bank === 'ca'
            ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100))
            : "",
          amount_bred: item.bank !== 'ca'
            ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100))
            : "",
          month_facture: {
            month: handleChangeDateToMonth(item.date_ech),
            ref: getRefFacture(item.numclient),
          },
          date_ech: new Date(item.date_ech).toLocaleDateString('en-CA'),
          payment_date: item.payment_date !== null
            ? new Date(item.payment_date).toLocaleDateString('en-CA')
            : null,
          contrat_valide: item.contrat_valide,
          first_reminder: handleGetReminder(item.validation, item.date_ech, 15, item.numclient),
          second_reminder: handleGetReminder(item.validation, item.date_ech, 30, item.numclient),
        }));

      const ht = processed.reduce((total, cur) => total + cur.total_ht, 0);
      const tva = processed.reduce((total, cur) => total + cur.montant_tva, 0);

      setTotalHt(numberToFormat(ht));
      setTotalTva(numberToFormat(tva));
      setTotalTtc(numberToFormat(ht + tva));

    } else {
      const source = updatedFacturesData || facturesData;

      processed = [...source].map(item => ({
        ...item,
        montant_tva: parseFloat((item.total_ht * (item.tva / 100)).toFixed(2)),
        amount_ca: item.bank === 'ca'
          ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100))
          : "",
        amount_bred: item.bank !== 'ca'
          ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100))
          : "",
        month_facture: {
          month: handleChangeDateToMonth(item.date_ech),
          ref: getRefFacture(item.numclient),
        },
        date_ech: new Date(item.date_ech).toLocaleDateString('en-CA'),
        contrat_valide: item.contrat_valide,
        first_reminder: handleGetReminder(item.validation, item.date_ech, 15, item.numclient),
        second_reminder: handleGetReminder(item.validation, item.date_ech, 30, item.numclient),
      }));
    }

    setFacturesData(processed);
    setFile(null);
  }, [dispatch, facAmendmentsByDossIndex, facturesData, handleGetReminder]);

  // ─── useEffect ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (dossierValue && facAmendmentsByDossIndex) {
      loadData();
    }
  }, [dossierValue, facAmendmentsByDossIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── handleChange ────────────────────────────────────────────────────────
  const handleChange = useCallback((event, index) => {
    setFacturesData(prev => {
      let updated = [...prev];
      const { name, value } = event.target;

      if (name === "contrat_valide") {
        const contratStatus = updated[index].contrat_valide == 0 ? 1 : 0;
        updated = updated.map(item => ({ ...item, contrat_valide: contratStatus }));

      } else if (name === "validation") {
        updated[index] = {
          ...updated[index],
          payment_date: updated[index].validation == 0 ? updated[index].payment_date : null,
          validation: updated[index].validation == 0 ? 1 : 0,
        };

      } else if (name === "payment_date") {
        updated[index] = { ...updated[index], payment_date: value, validation: 1 };
      }

      loadData(true, updated);
      return updated;
    });
  }, [loadData]);

  // ─── handleUpdateClick ───────────────────────────────────────────────────
  const handleUpdateClick = useCallback(async () => {
    await fetch("/api/facamendment/updateFacAmendmentByTable", {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ data: facturesData, ndossier: dossierValue }),
    }).catch(err => console.log(err));

    await dispatch(fetchAllFacAmendment());
  }, [dispatch, facturesData, dossierValue]);

  // ─── handleGenFacAmendmentTable ──────────────────────────────────────────
  const handleGenFacAmendmentTable = useCallback(async () => {
    handleUpdateClick();
    setFile(null);
    setDisableButton(true);

    const requestOptions = {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        factures: facturesData,
        ndossier: dossierValue,
        amendment_index: contractFacAmendmentIndex,
        total_ht: totalHt,
        total_tva: totalTva,
        total_ttc: totalTtc,
        date: new Date().toLocaleDateString('en-CA'),
      }),
    };

    const blob = await genFacAmendmentTable(requestOptions);
    if (blob) setFile(blob);
    setDisableButton(false);
    setOpenFile(true);
  }, [
    handleUpdateClick, facturesData, dossierValue,
    contractFacAmendmentIndex, totalHt, totalTva, totalTtc,
  ]);

  // ─── handleShowFacAmendmentTable ─────────────────────────────────────────
  const handleShowFacAmendmentTable = useCallback(async () => {
    setFile(null);
    setDisableButton(true);

    const response = await getFacAmendmentTable(dossierValue, contractFacAmendmentIndex, 'pdf');
    if (response.blob.size > 100) {
      setFile(response.blob);
      setOpenFile(true);
      dispatch(changePdfFileName(response.fileName));
    }
    setDisableButton(false);
  }, [dispatch, dossierValue, contractFacAmendmentIndex]);

  // ─── handleGetPdfFile ────────────────────────────────────────────────────
  const handleGetPdfFile = useCallback(async () => {
    setFile(null);
    const response = await getFacAmendmentTable(dossierValue, contractFacAmendmentIndex, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch, dossierValue, contractFacAmendmentIndex]);

  // ─── handleGetWordFile ───────────────────────────────────────────────────
  const handleGetWordFile = useCallback(async () => {
    setFile(null);
    const response = await getFacAmendmentTable(dossierValue, contractFacAmendmentIndex, 'word');
    const wordType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (response.blob.type === wordType) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch, dossierValue, contractFacAmendmentIndex]);

  // ─── closeDoc ────────────────────────────────────────────────────────────
  const closeDoc = useCallback(() => {
    setOpenFile(false);
    setFile(null);
  }, []);

  // ─── handleChangeTitleName ───────────────────────────────────────────────
  const handleChangeTitleName = useCallback(() => {
    dispatch(changeTitleName("Tableau de Facturation Avenant"));
  }, [dispatch]);

  // ─── render ──────────────────────────────────────────────────────────────
  return (
    <div className="table-fac-amendment">
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <NumDossFacAmendment />
      <h1 className="title">Tableau de Facturation Avenant</h1>
      <ListTable
        indexAmendmentLetter={indexAmendmentLetter}
        factures={facAmendmentsByDossIndex}
        facturesData={facturesData}
        totalHt={totalHt}
        totalTva={totalTva}
        totalTtc={totalTtc}
        disableButton={disableButton}
        loadData={loadData}
        handleChange={handleChange}
        handleUpdateClick={handleUpdateClick}
        handleGenFacAmendmentTable={handleGenFacAmendmentTable}
        handleShowFacAmendmentTable={handleShowFacAmendmentTable}
      />
      <GenPdf
        isOpen={openFile}
        file={file}
        closeDoc={closeDoc}
        scale={file !== null ? 1 : 0.5}
      />
    </div>
  );
};

export default FacAmendmentTable;